import { useEffect, useRef, useState } from "react";
import { getMessageTemplates } from "../services/api";
import { AI_NEXT_ACTIONS, formatAiNextAction } from "../utils/displayFormatters";
import { track } from "../services/analytics";
import LeadComposer from "./LeadComposer";
import LeadMessaging from "./LeadMessaging";

const ICON = {
  chat: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  phone:
    "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
  calendar:
    "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  note: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
};

const ActionIcon = ({ d }) => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

// Four equal, large actions. Text is the primary one.
const ACTION =
  "flex flex-1 flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors";
const SECONDARY = `${ACTION} border-border text-gray-700 hover:bg-gray-50`;
const PRIMARY = `${ACTION} border-primary bg-primary text-white hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50`;

const CHIP =
  "inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800";

/**
 * The effective next action and the AI's reason, with a Change link.
 * Send booking link fills the composer, Call back calls, the rest inform.
 * The staff value goes to next_action; ai_next_action is never written.
 */
const NextActionChip = ({ lead, bookingTemplate, onFillBooking, onChange }) => {
  const [changing, setChanging] = useState(false);
  const value = lead.next_action || lead.ai_next_action;
  if (!value) return null;
  const label = formatAiNextAction(value);

  // Inert values read as plain text, so only the two active ones look like buttons.
  let body = <span className="text-sm font-medium text-gray-700">{label}</span>;
  if (value === "send_booking_link" && !bookingTemplate) {
    body = <span className={CHIP}>{label}</span>;
  }
  if (value === "send_booking_link" && bookingTemplate) {
    body = (
      <button
        type="button"
        data-attr="next-action-booking"
        onClick={onFillBooking}
        className={`${CHIP} hover:shadow-md`}
      >
        {label}
      </button>
    );
  } else if (value === "call_back" && lead.phone) {
    body = (
      <a
        data-attr="next-action-call"
        href={`tel:${lead.phone}`}
        onClick={() => track("lead_call_clicked", { lead_id: lead.id })}
        className={`${CHIP} hover:shadow-md`}
      >
        {label}
      </a>
    );
  }

  return (
    <div data-testid="next-action-chip" className="px-3 pb-2">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
          Next
        </span>
        {body}
        {changing ? (
          <select
            aria-label="Change next action"
            value={lead.next_action || ""}
            onChange={(e) => {
              onChange(e.target.value);
              setChanging(false);
            }}
            className="rounded border-0 bg-gray-50 py-1 pl-2 pr-7 text-xs text-gray-700 focus:ring-2 focus:ring-primary"
          >
            <option value="">Use the AI's suggestion</option>
            {AI_NEXT_ACTIONS.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        ) : (
          <button
            type="button"
            onClick={() => setChanging(true)}
            className="text-xs text-primary hover:underline"
          >
            Change
          </button>
        )}
      </div>
      {lead.ai_follow_up_reason && (
        <p className="ph-mask mt-1 text-xs italic text-gray-500">{lead.ai_follow_up_reason}</p>
      )}
    </div>
  );
};

/**
 * The sidecart's one place to talk to a lead: action bar (Text, Call, Book,
 * Note), the thread when texts exist, and the composer. Replaces the act row
 * and the Messaging section in the sidecart.
 */
const ConversationCard = ({
  authToken,
  lead,
  refreshTrigger,
  onSent,
  onBook,
  onAddNote,
  onMessagesRead,
  onNextActionChange,
}) => {
  const textareaRef = useRef(null);
  const insertRef = useRef(null);
  const hasPhone = !!lead.phone;
  const canText = hasPhone && (lead.location?.messaging_enabled ?? false);
  const unread = lead.unread_message_count || 0;

  // The booking template is only needed for the booking chip.
  const [bookingTemplate, setBookingTemplate] = useState(null);
  const wantsBooking =
    canText && (lead.next_action || lead.ai_next_action) === "send_booking_link";
  useEffect(() => {
    // A new lead must never show the last lead's template.
    setBookingTemplate(null);
    if (!wantsBooking) return;
    let stale = false;
    getMessageTemplates()
      .then((response) => {
        if (stale) return;
        const all = response?.results || response || [];
        setBookingTemplate(all.find((t) => /booking/i.test(t.name)) || null);
      })
      // Without templates the chip only informs.
      .catch((err) => console.error("Failed to fetch templates:", err));
    return () => {
      stale = true;
    };
  }, [wantsBooking, lead.id]);

  const fillBooking = () => {
    track("lead_booking_chip_clicked", { lead_id: lead.id, had_template: true });
    insertRef.current?.(bookingTemplate);
  };

  return (
    <section data-testid="conversation-card" className="rounded-lg border border-border bg-white">
      <div className="flex items-center justify-between px-3 pt-3">
        <h3 className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
          Conversation
        </h3>
        {unread > 0 && (
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-800">
            {unread} unread
          </span>
        )}
      </div>

      <div className="flex items-stretch gap-2 p-3">
        <button
          type="button"
          data-attr="quick-action-text"
          disabled={!canText}
          onClick={() => textareaRef.current?.focus()}
          className={PRIMARY}
        >
          <ActionIcon d={ICON.chat} />
          Text
        </button>
        {hasPhone ? (
          <a
            data-attr="quick-action-call"
            href={`tel:${lead.phone}`}
            onClick={() => track("lead_call_clicked", { lead_id: lead.id })}
            className={SECONDARY}
          >
            <ActionIcon d={ICON.phone} />
            Call
          </a>
        ) : (
          <button type="button" data-attr="quick-action-call" disabled className={`${SECONDARY} opacity-40`}>
            <ActionIcon d={ICON.phone} />
            Call
          </button>
        )}
        <button type="button" data-attr="quick-action-book" onClick={onBook} className={SECONDARY}>
          <ActionIcon d={ICON.calendar} />
          Book
        </button>
        <button type="button" data-attr="quick-action-note" onClick={onAddNote} className={SECONDARY}>
          <ActionIcon d={ICON.note} />
          Note
        </button>
      </div>

      <NextActionChip
        lead={lead}
        bookingTemplate={canText ? bookingTemplate : null}
        onFillBooking={fillBooking}
        onChange={onNextActionChange}
      />

      <LeadMessaging
        card
        authToken={authToken}
        leadId={lead.id}
        lead={lead}
        onMessagesRead={onMessagesRead}
        refreshTrigger={refreshTrigger}
      />

      <div className="px-3 pb-3 pt-2">
        <LeadComposer
          authToken={authToken}
          lead={lead}
          onSent={onSent}
          autoFocus={!!lead.waiting_reply}
          textareaRef={textareaRef}
          insertRef={insertRef}
          surface="conversation_card"
          showTemplateChips
        />
      </div>
    </section>
  );
};

export default ConversationCard;
